/*
css
.accordion {
}

.accordion__body {
  overflow: hidden;
  transition: max-height 0.2s ease-out;
  max-height:0;
}


*/

import selectParent from 'select-parent'

// Body ids have to be unique across every accordion on the page.
let uid = 0

class Accordion {
	constructor(element, options) {
		this.options = {
			setHash: false,
			activeClass: 'is-active',
			toggleSync: false,
			singleOpen: false, // only one row open at a time
			a11y: true, // make the headers focusable toggles and keep aria in sync
			rowClass: '.accordion__row',
			headerClass: '.accordion__header',
			bodyClass: '.accordion__body',
			onChanged: (row, isExpanded) => {},
		}
		Object.assign(this.options, options)

		this.element = element
		// this.rows = Array.from(element.querySelectorAll(this.options.rowClass))
		this.rows = Array.from(element.querySelectorAll(this.options.rowClass)).map((row) => {
			return {
				el: row,
				isDisabled: row.classList.contains('is-disabled'),
				id: row.getAttribute('id'),
				elHeader: row.querySelector(this.options.headerClass),
				elBody: row.querySelector(this.options.bodyClass),
			}
		})

		// initiate listeners

		this.onClickBounded = this.onClick.bind(this)
		this.onExpandEndBounded = this.onExpandEnd.bind(this)
		this.onKeydownBounded = this.onKeydown.bind(this)
		this.element.addEventListener('click', this.onClickBounded)
		this.element.addEventListener('keydown', this.onKeydownBounded)
		this.init()
	}

	getRows() {
		return this.rows.map((row) => row.el)
	}

	// The headers are divs, so they need the button role, a tab stop and the
	// aria wiring to work as toggles. Rows marked `is-disabled` have nothing to
	// open, so they stay out of the tab order.
	setupA11y() {
		this.rows.forEach((row) => {
			if (!row.elHeader || !row.elBody) return

			if (row.isDisabled) {
				this.clearA11y(row)
				return
			}

			if (!row.elBody.getAttribute('id')) {
				row.elBody.setAttribute('id', `accordion-body-${++uid}`)
			}

			row.elHeader.setAttribute('role', 'button')
			row.elHeader.setAttribute('tabindex', '0')
			row.elHeader.setAttribute('aria-controls', row.elBody.getAttribute('id'))
			this.syncA11y(row.el, row.el.classList.contains(this.options.activeClass))
		})
	}

	clearA11y(row) {
		if (!row.elHeader) return

		row.elHeader.removeAttribute('role')
		row.elHeader.removeAttribute('tabindex')
		row.elHeader.removeAttribute('aria-controls')
		row.elHeader.removeAttribute('aria-expanded')
	}

	syncA11y(rowEl, isExpanded) {
		if (!this.options.a11y) return

		let header = rowEl.querySelector(this.options.headerClass)
		if (!header || !header.getAttribute('role')) return

		header.setAttribute('aria-expanded', isExpanded ? 'true' : 'false')
	}

	// A role="button" element does not fire click on Enter/Space the way a real
	// button does. Forwarding keeps one code path: the delegated click handler.
	onKeydown(ev) {
		if (!this.options.a11y) return
		if (ev.key !== 'Enter' && ev.key !== ' ') return

		let header = selectParent(this.options.headerClass, ev.target)
		if (!header && ev.target.classList.contains(this.options.headerClass.substr(1))) {
			header = ev.target
		}
		if (!header || !header.getAttribute('role')) return

		ev.preventDefault()
		header.click()
	}

	expandAll() {
		this.rows.forEach((row) => {
			this.expandRow(row.el)
		})
	}

	collapseAll() {
		this.rows.forEach((row) => {
			this.collapseRow(row.el)
		})
	}

	// Sets the browser hash
	setHash(id) {
		if (id && id !== '') {
			history.replaceState({}, null, `#${id}`)
			// window.location.hash = `#${id}`
		}
	}
	expandRow(row, noHistory) {
		if (this.options.setHash && !noHistory) {
			let id = row.getAttribute('id')
			if (id) {
				this.setHash(id)
			}
		}
		row.classList.add(this.options.activeClass)
		this.syncA11y(row, true)

		let rowBody = row.querySelector(this.options.bodyClass)

		// The pixel height is only a transition device: animate to the measured
		// height, then drop the cap so the open row behaves like any other
		// element and grows/shrinks with its content (resize, late image).
		rowBody.style.maxHeight = rowBody.scrollHeight + 'px'
		rowBody.addEventListener('transitionend', this.onExpandEndBounded, { once: true })

		setTimeout(() => this.options.onChanged(row, true), 0)
	}

	// Clearing the inline value hands the element back to CSS, where the
	// `max-height: 0` rule only applies to rows without the active class.
	onExpandEnd(ev) {
		if (ev.propertyName !== 'max-height') return

		let row = selectParent(this.options.rowClass, ev.target)
		// The row may have been closed again in the meantime — this same
		// listener would otherwise fire on the collapse and pop it back open.
		if (!row || !row.classList.contains(this.options.activeClass)) return

		ev.target.style.maxHeight = ''
	}

	expandRowById(id) {
		let row = this.rows.find((o) => {
			return o.id == id
		})

		if (!row) return

		this.expandRow(row.el, true)
	}

	collapseRow(row) {
		// if (triggerCallback) {
		//   this.options.onCollapseRow()
		// }

		if (!row.classList.contains(this.options.activeClass)) return

		row.classList.remove(this.options.activeClass)
		this.syncA11y(row, false)

		let rowBody = row.querySelector(this.options.bodyClass)

		// An open row is uncapped (see expandRow), and `max-height: none` is not
		// a value the browser can animate away from — it would jump straight to
		// 0. So commit the measured height with transitions suppressed, giving
		// the close a real length to start from, then animate down from it.
		rowBody.style.transition = 'none'
		rowBody.style.maxHeight = rowBody.scrollHeight + 'px'
		void rowBody.offsetHeight
		rowBody.style.transition = ''

		rowBody.style.maxHeight = '0px'

		setTimeout(() => this.options.onChanged(row, false), 0)
	}

	toggleRow(row) {
		if (row.classList.contains('is-disabled')) return
		if (!row.classList.contains(this.options.activeClass)) {
			if (this.options.toggleSync) {
				this.expandAll()
			} else {
				// Collapse first so the expanding row reports last, letting
				// listeners react to the final state without tracking it.
				if (this.options.singleOpen) {
					this.collapseAll()
				}
				this.expandRow(row)
			}
		} else {
			if (this.options.toggleSync) {
				this.collapseAll()
			} else {
				this.collapseRow(row)
			}
		}
	}

	onClick(ev) {
		let header = selectParent(this.options.headerClass, ev.target)
		if (!header && ev.target.classList.contains(this.options.headerClass.substr(1))) {
			header = ev.target
		}

		if (!header) return

		let row = selectParent(this.options.rowClass, ev.target)

		this.toggleRow(row)
	}
	setBaseMaxHeight() {
		this.rows.forEach((row) => {
			if (row.el.classList.contains(this.options.activeClass)) {
				row.elBody.style.maxHeight = ''
			} else {
				row.elBody.style.maxHeight = '0px'
			}
		})
	}

	reset() {
		this.element.removeEventListener('click', this.onClickBounded)
		this.element.removeEventListener('keydown', this.onKeydownBounded)
		this.rows.forEach((row) => {
			if (this.options.a11y) {
				this.clearA11y(row)
			}
			row.el.classList.remove(this.options.activeClass)
			row.elBody.style.maxHeight = 'auto'
			// remove maxHeight from the style
			row.elBody.style.maxHeight = ''
		})

		// el.classList.remove('is-enabled')
		// enabled = false
	}

	destroy() {
		// this.collapseAll()
		this.reset()
		// this.rows.forEach((row) => {

		//   console.log(row)
		//   row.elBody.style.maxHeight = '200px'
		// })
		// this.element.removeEventListener('click', this.onClickBounded)
	}

	init() {
		if (this.options.a11y) {
			this.setupA11y()
		}
		this.setBaseMaxHeight()
	}
}

export { Accordion }
